/**
 * dev-toolbox.ts — v3M13 Dev Toolbox panel.
 *
 * Bundles dev affordances behind a single backtick-toggled overlay:
 *   - Detection rings (mirrors F9, `ui.devDetectionOverlay`)
 *   - Enemy POV swap (mirrors F10, `ui.enemyPovOverlay`)
 *   - Reveal all (`ui.devRevealAll`) — bypasses FoW in renderers + VFX
 *   - +10 000 per resource (Credits / Minerals / Fuel / Research /
 *     Diplomatic / All) against the player empire, clamped at caps
 *
 * Strictly local UI state for the visibility flags — same MULTIPLAYER §2
 * reasoning as F9/F10 (zero command-stream side effects). Resource
 * grants directly mutate GameState, so they're a single-player cheat
 * path; v3M9 multiplayer will need a command path with server
 * arbitration.
 *
 * Mount pattern mirrors `research-panel.ts`: backdrop + panel created
 * once at session start, `update(state)` called per frame to keep
 * checkbox + label state synced, `destroy()` on session teardown.
 */

import { grantAllResourcesToPlayer, grantResourceToPlayer } from '../game/dev-grant';
import type { GameState } from '../state/types';
import type { UiState } from './state';

export interface DevToolboxHandle {
  update(state: GameState): void;
  destroy(): void;
}

export interface DevToolboxOptions {
  uiState: UiState;
}

export function mountDevToolbox(
  parent: HTMLElement,
  options: DevToolboxOptions,
): DevToolboxHandle {
  const { uiState } = options;

  // ── panel root ────────────────────────────────────────────────────────
  const panel = document.createElement('div');
  Object.assign(panel.style, {
    position: 'fixed',
    top: '12px',
    left: '12px',
    width: '260px',
    background: 'rgba(6,10,20,0.92)',
    border: '1px solid rgba(80,110,160,0.5)',
    borderRadius: '4px',
    padding: '10px 12px',
    color: '#d8e4ff',
    fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
    fontSize: '12px',
    lineHeight: '1.5',
    fontVariantNumeric: 'tabular-nums',
    display: 'none',
    zIndex: '800',
    pointerEvents: 'auto',
  } as Partial<CSSStyleDeclaration>);

  // ── header ────────────────────────────────────────────────────────────
  const header = document.createElement('div');
  header.textContent = 'DEV TOOLBOX';
  Object.assign(header.style, {
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.12em',
    color: '#8ea0c8',
    marginBottom: '8px',
    textTransform: 'uppercase',
  } as Partial<CSSStyleDeclaration>);
  panel.appendChild(header);

  const hint = document.createElement('div');
  hint.textContent = 'Press ` to toggle';
  Object.assign(hint.style, {
    fontSize: '10px',
    color: '#5d6b85',
    marginBottom: '12px',
  } as Partial<CSSStyleDeclaration>);
  panel.appendChild(hint);

  // ── visibility section ────────────────────────────────────────────────
  panel.appendChild(makeSectionLabel('Visibility'));

  const detectionRow = makeCheckboxRow('Detection rings (F9)', () => {
    uiState.devDetectionOverlay = !uiState.devDetectionOverlay;
  });
  panel.appendChild(detectionRow.row);

  const povRow = makeCheckboxRow('Enemy POV (F10)', () => {
    uiState.enemyPovOverlay = !uiState.enemyPovOverlay;
  });
  panel.appendChild(povRow.row);

  const revealRow = makeCheckboxRow('Reveal all', () => {
    uiState.devRevealAll = !uiState.devRevealAll;
  });
  panel.appendChild(revealRow.row);

  // ── resources section ─────────────────────────────────────────────────
  const resourceLabel = makeSectionLabel('Player Resources');
  resourceLabel.style.marginTop = '12px';
  panel.appendChild(resourceLabel);

  let lastSeenState: GameState | null = null;
  function withState(fn: (s: GameState) => void): void {
    if (lastSeenState !== null) fn(lastSeenState);
  }

  const grantGrid = document.createElement('div');
  Object.assign(grantGrid.style, {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '6px',
  } as Partial<CSSStyleDeclaration>);
  panel.appendChild(grantGrid);

  grantGrid.appendChild(
    makeGrantButton('+10k Credits', () =>
      withState((s) => grantResourceToPlayer(s, 'credits')),
    ),
  );
  grantGrid.appendChild(
    makeGrantButton('+10k Minerals', () =>
      withState((s) => grantResourceToPlayer(s, 'minerals')),
    ),
  );
  grantGrid.appendChild(
    makeGrantButton('+10k Fuel', () =>
      withState((s) => grantResourceToPlayer(s, 'fuel')),
    ),
  );
  grantGrid.appendChild(
    makeGrantButton('+10k Research', () =>
      withState((s) => grantResourceToPlayer(s, 'research')),
    ),
  );
  grantGrid.appendChild(
    makeGrantButton('+10k Diplomatic', () =>
      withState((s) => grantResourceToPlayer(s, 'diplomatic')),
    ),
  );
  grantGrid.appendChild(
    makeGrantButton('+10k All', () => withState((s) => grantAllResourcesToPlayer(s))),
  );

  parent.appendChild(panel);

  // ── update / destroy ──────────────────────────────────────────────────
  function update(state: GameState): void {
    lastSeenState = state;
    panel.style.display = uiState.devToolboxOpen ? 'block' : 'none';
    if (!uiState.devToolboxOpen) return;
    detectionRow.checkbox.checked = uiState.devDetectionOverlay;
    povRow.checkbox.checked = uiState.enemyPovOverlay;
    revealRow.checkbox.checked = uiState.devRevealAll;
  }

  function destroy(): void {
    panel.remove();
  }

  return { update, destroy };
}

// ─── helpers ────────────────────────────────────────────────────────────

function makeSectionLabel(text: string): HTMLElement {
  const el = document.createElement('div');
  el.textContent = text;
  Object.assign(el.style, {
    fontSize: '10px',
    color: '#7e8799',
    letterSpacing: '0.10em',
    textTransform: 'uppercase',
    marginBottom: '6px',
  } as Partial<CSSStyleDeclaration>);
  return el;
}

interface CheckboxRow {
  row: HTMLElement;
  checkbox: HTMLInputElement;
}

function makeCheckboxRow(label: string, onToggle: () => void): CheckboxRow {
  const row = document.createElement('label');
  Object.assign(row.style, {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 0',
    cursor: 'pointer',
    userSelect: 'none',
  } as Partial<CSSStyleDeclaration>);
  const checkbox = document.createElement('input');
  checkbox.type = 'checkbox';
  Object.assign(checkbox.style, {
    accentColor: '#3F6FA8',
  } as Partial<CSSStyleDeclaration>);
  checkbox.addEventListener('change', () => onToggle());
  const text = document.createElement('span');
  text.textContent = label;
  row.appendChild(checkbox);
  row.appendChild(text);
  return { row, checkbox };
}

function makeGrantButton(label: string, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = label;
  Object.assign(btn.style, {
    background: '#0E131C',
    color: '#D7DEEA',
    border: '1px solid #2A3344',
    borderRadius: '2px',
    padding: '6px 8px',
    fontFamily: 'inherit',
    fontSize: '11px',
    letterSpacing: '0.04em',
    cursor: 'pointer',
  } as Partial<CSSStyleDeclaration>);
  // pointerdown to dodge the rebuild-race noted in detail-panel.ts.
  btn.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    onClick();
  });
  return btn;
}
